import React from 'react';
import classNames from 'classnames';
import { FormSpy } from 'react-final-form';

import { useIntl } from '../../../util/reactIntl';
import { parseSelectFilterOptions } from '../../../util/search';
import { FieldCheckbox } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import PickupRadiusFilter from '../PickupRadiusFilter/PickupRadiusFilter';
import selectCss from '../SelectMultipleFilter/SelectMultipleFilter.module.css';

import css from './DeliveryFilter.module.css';
import {
  SHIPPING_PARAM,
  PICKUP_PARAM,
  SHIPPING_FIELD,
  PICKUP_FIELD,
  isParamActive,
  toUrlParams,
  clearUrlParams,
} from './deliveryFilterHelpers';

export { SHIPPING_PARAM, PICKUP_PARAM } from './deliveryFilterHelpers';

const toFormOptions = paramValue =>
  isParamActive(paramValue) ? parseSelectFilterOptions('true') : [];

const PickupPanelAnimated = props => {
  const { urlQueryParams, onRadiusSubmit, id } = props;

  return (
    <FormSpy subscription={{ values: true }}>
      {({ values }) => {
        const pickupChecked = values?.[PICKUP_FIELD]?.includes?.('true');
        return (
          <div
            className={classNames(css.pickupPanel, pickupChecked && css.pickupPanelOpen)}
            aria-hidden={!pickupChecked}
          >
            <div className={css.pickupPanelInner}>
              <PickupRadiusFilter
                id={id}
                urlQueryParams={urlQueryParams}
                onSubmit={onRadiusSubmit}
                inactive={!pickupChecked}
              />
            </div>
          </div>
        );
      }}
    </FormSpy>
  );
};

/**
 * Combined shipping + pickup filter section with nested pickup radius controls.
 */
const DeliveryFilter = props => {
  const intl = useIntl();
  const {
    className,
    rootClassName,
    id,
    containerId,
    urlQueryParams = {},
    onSubmit,
    onRadiusSubmit,
    getAriaLabel,
  } = props;

  const shippingSelected = isParamActive(urlQueryParams[SHIPPING_PARAM]);
  const pickupSelected = isParamActive(urlQueryParams[PICKUP_PARAM]);
  const isSelected = shippingSelected || pickupSelected;
  const selectedCount = [shippingSelected, pickupSelected].filter(Boolean).length;

  const label = intl.formatMessage({ id: 'DeliveryFilter.label' });

  const initialValues = {
    [SHIPPING_FIELD]: toFormOptions(urlQueryParams[SHIPPING_PARAM]),
    [PICKUP_FIELD]: toFormOptions(urlQueryParams[PICKUP_PARAM]),
  };

  const handleSubmit = values => {
    if (values === null) {
      onSubmit(clearUrlParams());
      return;
    }
    onSubmit(toUrlParams(values));
  };

  const deliveryOptions = [
    {
      field: SHIPPING_FIELD,
      option: 'true',
      labelId: 'DeliveryFilter.shippingLabel',
      fieldIdSuffix: 'shipping',
    },
    {
      field: PICKUP_FIELD,
      option: 'true',
      labelId: 'DeliveryFilter.pickupLabel',
      fieldIdSuffix: 'pickup',
    },
  ];

  return (
    <FilterPlain
      className={className}
      rootClassName={classNames(css.deliveryFilterRoot, rootClassName)}
      label={label}
      ariaLabel={getAriaLabel(label, selectedCount > 0 ? String(selectedCount) : '')}
      isSelected={isSelected}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={initialValues}
      containerId={containerId}
    >
      <fieldset className={selectCss.fieldGroupPlain}>
        <legend className={selectCss.accessibilityLegend}>{label}</legend>
        <ul className={selectCss.list}>
          {deliveryOptions.map(({ field, option, labelId, fieldIdSuffix }) => (
            <li key={fieldIdSuffix} className={selectCss.item}>
              <FieldCheckbox
                id={`${id}-${fieldIdSuffix}`}
                name={field}
                label={intl.formatMessage({ id: labelId })}
                value={option}
                compact
                rootClassName={selectCss.compactCheckbox}
                labelClassName={selectCss.plainLabel}
                textClassName={selectCss.plainLabelText}
              />
            </li>
          ))}
        </ul>
      </fieldset>
      <PickupPanelAnimated
        id={`${id}.pickupRadius`}
        urlQueryParams={urlQueryParams}
        onRadiusSubmit={onRadiusSubmit}
      />
    </FilterPlain>
  );
};

export default DeliveryFilter;
